import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DollarSign, TrendingUp, TrendingDown, CreditCard, Receipt, ShieldCheck } from 'lucide-react';
import type { PurchaseOrder, POModificationRequest } from '@/lib/types/job';
import { usePlatformSetting } from '@/hooks/usePlatformSettings';
import { useUserRole } from '@/hooks/useUserRole';
import { useInitialPaymentLedger } from '@/hooks/useInitialPaymentLedger';
import { usePaymentTransaction } from '@/hooks/usePaymentTransactions';
import { useTransactionTaxDetails } from '@/hooks/useTaxPreview';
import { usePaymentProcessingEnabled } from '@/hooks/usePaymentProcessingEnabled';

interface FinancialSummaryCardProps {
  purchaseOrder: PurchaseOrder;
  adjustments: POModificationRequest[];
}

export function FinancialSummaryCard({ purchaseOrder, adjustments }: FinancialSummaryCardProps) {
  const { isVendor } = useUserRole();
  const { data: platformFeeSetting } = usePlatformSetting('platform_fee_percentage');
  
  // Use the Financial Ledger as the single source of truth for initial payment breakdown
  // This provides consistent data across ALL payment types (card, ACH, invoice, traditional ACH)
  const { data: initialPayment } = useInitialPaymentLedger(purchaseOrder.job_id, purchaseOrder.id);
  
  // Keep transaction query for tax details breakdown display only
  const { data: transaction } = usePaymentTransaction(purchaseOrder.job_id);
  const { data: taxDetails } = useTransactionTaxDetails(transaction?.id);

  const { paymentProcessingEnabled } = usePaymentProcessingEnabled();

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  };

  // Calculate applied adjustments (accepted or applied)
  const appliedAdjustments = adjustments.filter(
    adj => adj.status === 'accepted' || adj.applied_at
  );
  
  // Sum all adjustment amounts, handling potential undefined values
  const totalAdjustmentCents = appliedAdjustments.reduce((sum, adj) => {
    return sum + (adj.adjustment_amount_cents ?? 0);
  }, 0);

  // Get initial payment breakdown from ledger (single source of truth)
  // Falls back to calculating from current PO amount if ledger data unavailable
  const finalAmountCents = purchaseOrder.po_amount_cents;
  
  // Ledger-based amounts for initial payment display
  const initialSubtotalCents = initialPayment?.subtotalCents ?? 0;
  const initialTaxCents = initialPayment?.taxCents ?? 0;
  const initialCcFeeCents = initialPayment?.ccFeeCents ?? 0;
  const initialTotalChargedCents = initialPayment?.totalChargedCents ?? 0;
  
  // Original PO amount for the summary cards (subtotal without tax)
  // If we have ledger data, use subtotal; otherwise fall back to calculation
  const originalAmountCents = initialPayment 
    ? initialSubtotalCents
    : (finalAmountCents - totalAdjustmentCents);
  
  // Get vendor tax amount (embedded on PO or from bid) for current final amount
  const vendorTaxCents = (purchaseOrder as any).total_vendor_tax_cents || 
                         (purchaseOrder as any).bid?.total_vendor_tax_cents || 0;
  
  // Final amount including tax
  const finalAmountWithTaxCents = finalAmountCents + vendorTaxCents;
  
  // Check for pending credits (negative adjustments that would result in a refund)
  const pendingRefundCents = totalAdjustmentCents < 0 ? Math.abs(totalAdjustmentCents) : 0;

  // Calculate vendor payout (vendor-only)
  const feePercentage = parseFloat(platformFeeSetting?.setting_value || '7');  // Fallback to 7% platform fee
  const platformFeeCents = Math.floor(finalAmountCents * (feePercentage / 100));
  // Vendor payout: base amount minus fee, plus tax (tax is passed through to vendor)
  const vendorPayoutCents = (finalAmountCents - platformFeeCents) + vendorTaxCents;

  // Flags for conditional rendering
  const hasTax = initialTaxCents > 0;
  const hasCcFee = initialCcFeeCents > 0;
  const hasInitialPaymentData = !!initialPayment;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="h-5 w-5" />
          Financial Summary
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          {/* Original PO Amount */}
          <div>
            <div className="text-sm text-muted-foreground mb-1">Original PO Amount</div>
            <div className="text-2xl font-semibold">
              {formatCurrency(originalAmountCents)}
            </div>
          </div>

          {/* Total Change Orders */}
          <div>
            <div className="text-sm text-muted-foreground mb-1 flex items-center gap-2">
              Total Change Orders
              {appliedAdjustments.length > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {appliedAdjustments.length}
                </Badge>
              )}
            </div>
            <div className={`text-2xl font-semibold flex items-center gap-2 ${
              totalAdjustmentCents > 0 ? 'text-orange-600' : 
              totalAdjustmentCents < 0 ? 'text-green-600' : 
              'text-muted-foreground'
            }`}>
              {totalAdjustmentCents !== 0 && (
                totalAdjustmentCents > 0 ? (
                  <TrendingUp className="h-5 w-5" />
                ) : (
                  <TrendingDown className="h-5 w-5" />
                )
              )}
              {totalAdjustmentCents >= 0 ? '+' : ''}{formatCurrency(totalAdjustmentCents)}
            </div>
          </div>

          {/* Tax Amount */}
          <div>
            <div className="text-sm text-muted-foreground mb-1">Tax Amount</div>
            <div className="text-2xl font-semibold text-orange-600">
              +{formatCurrency(vendorTaxCents)}
            </div>
          </div>

          {/* Final Amount */}
          <div>
            <div className="text-sm text-muted-foreground mb-1">Final PO Amount</div>
            <div className="text-2xl font-semibold text-primary">
              {formatCurrency(finalAmountWithTaxCents)}
            </div>
          </div>
        </div>

        {/* Client-Only Initial Payment Details - Using Financial Ledger as source of truth */}
        {!isVendor && hasInitialPaymentData && paymentProcessingEnabled && (
          <div className="mt-6 pt-4 border-t">
            <div className="text-sm font-medium text-muted-foreground mb-3">Initial Payment Details</div>
            <div className="space-y-2">
              {/* Initial PO Amount (subtotal from ledger - excludes tax) */}
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">Initial PO Amount</span>
                <span>{formatCurrency(initialSubtotalCents)}</span>
              </div>
              
              {/* Tax Breakdown - uses taxDetails for per-location breakdown if available */}
              {hasTax && taxDetails && taxDetails.length > 0 && (
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Receipt className="h-3.5 w-3.5" />
                    Sales Tax
                  </div>
                  {taxDetails.map((detail: any, idx: number) => (
                    <div key={idx} className="flex justify-between items-center text-xs pl-5">
                      <span className="text-muted-foreground flex items-center gap-1">
                        {detail.delivery_location?.delivery_name || detail.jurisdiction_state || 'Location'}
                        {detail.is_exempt && (
                          <Badge variant="outline" className="text-[10px] py-0 px-1">
                            <ShieldCheck className="h-2.5 w-2.5 mr-0.5" />
                            Exempt
                          </Badge>
                        )}
                      </span>
                      <span>{formatCurrency(detail.tax_amount_cents)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center text-sm pt-1">
                    <span className="text-muted-foreground font-medium">Total Tax</span>
                    <span className="text-orange-600">{formatCurrency(initialTaxCents)}</span>
                  </div>
                </div>
              )}
              
              {/* Single tax line if no breakdown available */}
              {hasTax && (!taxDetails || taxDetails.length === 0) && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground flex items-center gap-1.5">
                    <Receipt className="h-3.5 w-3.5" />
                    Sales Tax
                  </span>
                  <span className="text-orange-600">{formatCurrency(initialTaxCents)}</span>
                </div>
              )}
              
              {/* CC Processing Fee (from ledger) */}
              {hasCcFee && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground flex items-center gap-1.5">
                    <CreditCard className="h-3.5 w-3.5" />
                    Credit Card Processing Fee
                  </span>
                  <span className="text-orange-600">{formatCurrency(initialCcFeeCents)}</span>
                </div>
              )}
              
              {/* Divider */}
              <div className="border-t my-2" />
              
              {/* Total Initially Charged (from ledger - already includes all components) */}
              <div className="flex justify-between items-center">
                <span className="font-medium">Total Initially Charged</span>
                <span className="text-lg font-semibold">
                  {formatCurrency(initialTotalChargedCents)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Vendor-Only Payout Breakdown */}
        {isVendor && paymentProcessingEnabled && (
          <div className="mt-6 pt-4 border-t">
            <div className="text-sm font-medium text-muted-foreground mb-3">Your Payout Breakdown</div>
            <div className="space-y-2">
              {/* Subtotal */}
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span>{formatCurrency(finalAmountCents)}</span>
              </div>
              
              {/* Platform Fee */}
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">Platform Fee ({feePercentage}%)</span>
                <span className="text-red-600">-{formatCurrency(platformFeeCents)}</span>
              </div>
              
              {/* Sales Tax (passed through to vendor) */}
              {vendorTaxCents > 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Sales Tax</span>
                  <span className="text-green-600">+{formatCurrency(vendorTaxCents)}</span>
                </div>
              )}
              
              {/* Divider */}
              <div className="border-t my-2" />
              
              {/* Your Payout */}
              <div className="flex justify-between items-center">
                <span className="font-medium">Your Payout</span>
                <span className="text-lg font-semibold text-green-600">
                  {formatCurrency(vendorPayoutCents)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Pending Refund Notice */}
        {pendingRefundCents > 0 && paymentProcessingEnabled && (
          <div className="mt-4 p-3 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900 rounded-md">
            <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
              <DollarSign className="h-4 w-4" />
              <span className="font-medium">Pending Refund: {formatCurrency(pendingRefundCents)}</span>
            </div>
            <p className="text-xs text-green-600 dark:text-green-300 mt-1">
              This amount will be refunded when the job is completed and accepted.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
